import { ArrowLeft, ArrowRight, SkipForward } from 'lucide-react';

import { Badge } from '@/components/common/Badge/Badge';
import { Button } from '@/components/common/Button/Button';
import { ProgressBar } from '@/components/common/ProgressBar/ProgressBar';
import { AnswerOption } from '@/components/positions/AnswerOption/AnswerOption';
import type { PoliticalMatchAnswerValue, PoliticalMatchQuestion } from '@/models/political-match';
import { getPoliticalMatchAnswerLabel, getPoliticalMatchAxisLabel } from '@/utils/political-match';

import styles from '@/styles/positions.module.css';

type PoliticalMatchQuestionStepProps = {
  question: PoliticalMatchQuestion;
  currentQuestionNumber: number;
  totalQuestions: number;
  selectedAnswer?: PoliticalMatchAnswerValue;
  canGoNext: boolean;
  onSelectAnswer: (answer: Exclude<PoliticalMatchAnswerValue, null>) => void;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
};

const answerOptions: Array<Exclude<PoliticalMatchAnswerValue, null>> = [-2, -1, 0, 1, 2];

export function PoliticalMatchQuestionStep({
  question,
  currentQuestionNumber,
  totalQuestions,
  selectedAnswer,
  canGoNext,
  onSelectAnswer,
  onBack,
  onNext,
  onSkip,
}: PoliticalMatchQuestionStepProps): JSX.Element {
  const progressValue = totalQuestions === 0 ? 0 : (currentQuestionNumber / totalQuestions) * 100;

  return (
    <section className={styles.questionCard}>
      <div className={styles.questionHeader}>
        <div className={styles.questionProgress}>
          <div className={styles.questionProgressRow}>
            <span className={styles.questionCount}>
              {currentQuestionNumber} de {totalQuestions}
            </span>
            <div className={styles.questionBadges}>
              <Badge tone="primary">{getPoliticalMatchAxisLabel(question.axis)}</Badge>
              <Badge tone="neutral">{question.dimension}</Badge>
            </div>
          </div>

          <ProgressBar value={progressValue} label={`Progresso: ${currentQuestionNumber} de ${totalQuestions}`} />
        </div>

        <p className={styles.questionPrompt}>{question.statement}</p>
      </div>

      <div className={styles.questionOptions}>
        {answerOptions.map((value) => (
          <AnswerOption
            key={value}
            label={getPoliticalMatchAnswerLabel(value)}
            selected={selectedAnswer === value}
            value={value}
            analyticsScope={`match:question:${question.id}`}
            onSelect={() => onSelectAnswer(value)}
          />
        ))}
      </div>

      <div className={styles.questionActions}>
        <div className={styles.questionActionsGroup}>
          <Button type="button" variant="secondary" onClick={onBack}>
            <ArrowLeft size={16} />
            Voltar
          </Button>
          <Button type="button" variant="ghost" onClick={onSkip}>
            <SkipForward size={16} />
            Pular
          </Button>
        </div>

        <Button type="button" onClick={onNext} disabled={!canGoNext}>
          Próxima
          <ArrowRight size={16} />
        </Button>
      </div>
    </section>
  );
}
